// Layer 3 (Transformation): shift-start brief lines per role (cro-brief R1, #328).

import type { Case, RecommendedAction } from '@/versions/v1/schema/case';
import type { PlantRoleId } from '@/versions/v1/schema/plant-role';
import type { SeriesTime } from '@/versions/v1/schema/reading-series';
import type { SessionState } from '@/versions/v1/schema/scenario';
import { outcomeForCase } from '@/versions/v1/modules/advisory/Scene.transform';
import { CASE_OUTCOMES, FMEA_UPDATE_REVIEWS } from '@/versions/v1/shared/bunker-hill-advisory-seed';
import {
  asCaseTime,
  clockOnProductionDay,
  diffMinutes,
  previousShiftWindow,
  shiftWindowBefore,
  type ShiftWindow,
} from '@/versions/v1/shared/clock.derive';
import { windowContains } from '@/versions/v1/shared/roster-period.derive';
import {
  casesForRole,
  isMaintenanceSeatCase,
  type CasesForRoleOptions,
} from '@/versions/v1/shared/role-scope';

export type ShiftBriefLine = {
  at: string;
  verb: string;
  title: string;
  caseId: string;
  flagTail: string;
  /** Outcome sentence for a closed case; rendered in the line's tooltip. */
  outcome?: string;
  sortTime: SeriesTime;
  severityRank: number;
};

export type ShiftBriefRead = {
  title: string;
  lines: readonly ShiftBriefLine[];
  extended: boolean;
  countLine?: string;
};

const SEVERITY_RANK: Record<Case['severity'], number> = { high: 0, med: 1, low: 2, monitor: 3 };

const CLOSED_STATES: readonly Case['state'][] = ['approved', 'rejected', 'closed'];

function resolveStamp(stamp: string, cursor: SeriesTime): SeriesTime {
  return stamp.includes('T') ? asCaseTime(stamp) : clockOnProductionDay(stamp, cursor);
}

function inWindow(stamp: string, cursor: SeriesTime, window: ShiftWindow): boolean {
  return windowContains(stamp, cursor, window);
}

function briefCaseScope(
  role: PlantRoleId,
  cases: readonly Case[],
  options: CasesForRoleOptions = {},
): Case[] {
  if (role === 'cro') {
    return cases.filter((c) => c.assignedTo === 'cro' || c.assignedTo === 'operator');
  }
  if (role === 'metallurgist') {
    return cases.filter((c) => {
      if (c.assignedTo === 'metallurgist') return true;
      if (isMaintenanceSeatCase(c)) return false;
      return casesForRole('metallurgist', [c]).length > 0;
    });
  }
  if (role === 'gm') {
    return cases.filter(
      (c) =>
        !CLOSED_STATES.includes(c.state) ||
        (c.resolvedAt && c.severity !== 'monitor') ||
        casesForRole('gm', cases, { reviews: options.reviews ?? FMEA_UPDATE_REVIEWS }).some(
          (row) => row.id === c.id,
        ),
    );
  }
  return casesForRole(role, cases, options);
}

function flagTailFor(caseItem: Case, verb: string): string {
  if (verb === 'closed') {
    const outcome = outcomeForCase(caseItem.id);
    const tail = outcome?.effectiveness ?? caseItem.state.replace(/-/g, ' ');
    return tail.slice(0, 48);
  }
  if (caseItem.branch === 'zero-touch') return 'zero-touch';
  if (caseItem.severity === 'high') return 'high';
  if (caseItem.severity === 'med') return 'med';
  return caseItem.state.replace(/-/g, ' ');
}

function windowIsLastShift(window: ShiftWindow, cursor: SeriesTime): boolean {
  const prior = previousShiftWindow(cursor);
  return window.start === prior.start;
}

function formatBriefClock(
  stamp: string,
  cursor: SeriesTime,
  lastShiftOnly: boolean,
): string {
  const full = resolveStamp(stamp, cursor);
  const [date, time] = full.split('T');
  const clock = time?.slice(0, 5) ?? '00:00';
  if (lastShiftOnly) return clock;
  // A multi-day window day-qualifies every stamp so the column reads as one format.
  const [, month, day] = (date ?? '').split('-');
  const monthLabel = BRIEF_MONTHS[Number(month) - 1] ?? month ?? '';
  return `${day ?? ''} ${monthLabel} ${clock}`;
}

const BRIEF_MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'] as const;

function collectEvents(
  scopedCases: readonly Case[],
  actions: readonly RecommendedAction[],
  cursor: SeriesTime,
  window: ShiftWindow,
  lastShiftOnly: boolean,
): ShiftBriefLine[] {
  const lines: ShiftBriefLine[] = [];

  for (const caseItem of scopedCases) {
    if (inWindow(caseItem.raisedAt, cursor, window)) {
      lines.push({
        at: formatBriefClock(caseItem.raisedAt, cursor, lastShiftOnly),
        verb: 'raised',
        title: caseItem.title,
        caseId: caseItem.id,
        flagTail: flagTailFor(caseItem, 'raised'),
        sortTime: resolveStamp(caseItem.raisedAt, cursor),
        severityRank: SEVERITY_RANK[caseItem.severity],
      });
    }

    if (caseItem.state === 'escalated' && inWindow(caseItem.raisedAt, cursor, window)) {
      lines.push({
        at: formatBriefClock(caseItem.raisedAt, cursor, lastShiftOnly),
        verb: 'escalated',
        title: caseItem.title,
        caseId: caseItem.id,
        flagTail: flagTailFor(caseItem, 'escalated'),
        sortTime: resolveStamp(caseItem.raisedAt, cursor),
        severityRank: SEVERITY_RANK[caseItem.severity],
      });
    }

    if (caseItem.resolvedAt && inWindow(caseItem.resolvedAt, cursor, window)) {
      const outcome = CASE_OUTCOMES.find((row) => row.caseId === caseItem.id);
      lines.push({
        at: formatBriefClock(caseItem.resolvedAt, cursor, lastShiftOnly),
        verb: 'closed',
        title: caseItem.title,
        caseId: caseItem.id,
        flagTail: flagTailFor(caseItem, 'closed'),
        outcome: outcome?.actionTaken,
        sortTime: resolveStamp(caseItem.resolvedAt, cursor),
        severityRank: SEVERITY_RANK[caseItem.severity],
      });
    }
  }

  for (const action of actions) {
    if (!action.decision) continue;
    const caseItem = scopedCases.find((c) => c.id === action.caseId);
    if (!caseItem) continue;
    if (!inWindow(action.decision.at, cursor, window)) continue;
    lines.push({
      at: formatBriefClock(action.decision.at, cursor, lastShiftOnly),
      verb: 'decision taken',
      title: caseItem.title,
      caseId: caseItem.id,
      flagTail: action.status,
      sortTime: resolveStamp(action.decision.at, cursor),
      severityRank: SEVERITY_RANK[caseItem.severity],
    });
  }

  return lines;
}

/** One line per case — the latest event inside the window (R1). */
function oneLinePerCase(lines: ShiftBriefLine[]): ShiftBriefLine[] {
  const byCase = new Map<string, ShiftBriefLine>();
  for (const line of lines) {
    const existing = byCase.get(line.caseId);
    if (!existing || diffMinutes(line.sortTime, existing.sortTime) > 0) {
      byCase.set(line.caseId, line);
    }
  }
  return sortLines(Array.from(byCase.values()));
}

function sortLines(lines: ShiftBriefLine[]): ShiftBriefLine[] {
  return [...lines].sort((a, b) => {
    const sev = a.severityRank - b.severityRank;
    if (sev !== 0) return sev;
    return diffMinutes(a.sortTime, b.sortTime);
  });
}

function extendedTitle(window: ShiftWindow): string {
  const d = window.start.split('T')[0]!;
  const day = Number.parseInt(d.split('-')[2]!, 10);
  const mon = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'][
    Number.parseInt(d.split('-')[1]!, 10) - 1
  ]!;
  const kind = window.kind === 'night' ? 'night' : 'day';
  return `Since ${String(day).padStart(2, '0')} ${mon} ${kind}`;
}

export function shiftBriefEventCount(
  state: SessionState,
  cases: readonly Case[],
  actions: readonly RecommendedAction[],
  role: PlantRoleId,
  window: ShiftWindow,
): { raised: number; closed: number; decisions: number; total: number } {
  const scoped = briefCaseScope(role, cases, { reviews: FMEA_UPDATE_REVIEWS });
  const lastShiftOnly = windowIsLastShift(window, state.cursor);
  const raw = collectEvents(scoped, actions, state.cursor, window, lastShiftOnly);
  return {
    raised: raw.filter((line) => line.verb === 'raised').length,
    closed: raw.filter((line) => line.verb === 'closed').length,
    decisions: raw.filter((line) => line.verb === 'decision taken').length,
    total: raw.length,
  };
}

function countEvents(
  scopedCases: readonly Case[],
  actions: readonly RecommendedAction[],
  cursor: SeriesTime,
  window: ShiftWindow,
  lastShiftOnly: boolean,
): { raised: number; closed: number; decisions: number; total: number } {
  const raw = collectEvents(scopedCases, actions, cursor, window, lastShiftOnly);
  return {
    raised: raw.filter((line) => line.verb === 'raised').length,
    closed: raw.filter((line) => line.verb === 'closed').length,
    decisions: raw.filter((line) => line.verb === 'decision taken').length,
    total: raw.length,
  };
}

export function shiftBriefRead(
  state: SessionState,
  cases: readonly Case[],
  actions: readonly RecommendedAction[],
  role: PlantRoleId,
  window: ShiftWindow = previousShiftWindow(state.cursor),
  title = 'Since your last shift',
): ShiftBriefRead {
  const cursor = state.cursor;
  const scoped = briefCaseScope(role, cases, { reviews: FMEA_UPDATE_REVIEWS });
  let activeWindow = window;
  const lastShiftOnly = windowIsLastShift(activeWindow, cursor);
  let lines = oneLinePerCase(collectEvents(scoped, actions, cursor, activeWindow, lastShiftOnly));
  let extended = false;

  const isLastShift = lastShiftOnly;

  if (isLastShift && lines.length < 3) {
    const prior = shiftWindowBefore(activeWindow);
    lines = oneLinePerCase([
      ...collectEvents(scoped, actions, cursor, prior, false),
      ...collectEvents(scoped, actions, cursor, activeWindow, true),
    ]);
    activeWindow = prior;
    extended = true;
  }

  const counts = countEvents(scoped, actions, cursor, activeWindow, windowIsLastShift(activeWindow, cursor));
  const visibleLines = lines.slice(0, 5);
  const eventTotal = counts.raised + counts.closed + counts.decisions;
  const countCore = `${counts.raised} raised · ${counts.closed} closed · ${counts.decisions} decisions`;
  const countLine =
    lines.length > 5
      ? `showing ${visibleLines.length} of ${eventTotal} · ${countCore}`
      : countCore;

  return {
    title: extended ? extendedTitle(activeWindow) : title,
    lines: visibleLines,
    extended,
    countLine,
  };
}
